import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, Typography, Button, Avatar, Paper, Snackbar, Alert, 
  ThemeProvider, createTheme, CssBaseline, Tabs, Tab,
  Card, CardContent, Grid, Chip, IconButton, LinearProgress,
  Fade, Zoom, Grow, Slide, Dialog, DialogTitle, DialogContent,
  DialogActions, List, ListItem, ListItemText, ListItemIcon,
  Badge, Tooltip, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TablePagination
} from '@mui/material';
import {
  QrCodeScanner,
  Phone,
  Person,
  CheckCircle,
  Error,
  AccessTime,
  Work,
  Coffee,
  ExitToApp,
  Refresh,
  Close,
  WifiOff,
  Wifi,
  CloudSync,
  CloudOff,
  Refresh as Retry,
  Warning,
  Info,
  Schedule,
  TrendingUp,
  CalendarToday
} from '@mui/icons-material';
import { Html5QrcodeScanner } from 'html5-qrcode';
import axiosInstance from '../axiosConfig';
import { useLanguage } from '../../contexts/LanguageContext';
import networkManager from '../../utils/networkManager';
import offlineStorage from '../../utils/offlineStorage';
import moment from 'moment';

// Modern dark theme with gradients
const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#667eea',
      light: '#8b9df6',
      dark: '#4c63d2',
    },
    secondary: {
      main: '#764ba2',
      light: '#9b6bc3',
      dark: '#5a3d7a',
    },
    background: {
      default: '#0a0a0a',
      paper: 'rgba(255, 255, 255, 0.05)',
    },
    text: {
      primary: '#ffffff',
      secondary: '#b3b3b3',
    },
    success: {
      main: '#4caf50',
      light: '#81c784',
      dark: '#388e3c',
    },
    error: {
      main: '#f44336',
      light: '#e57373',
      dark: '#d32f2f',
    },
    warning: {
      main: '#ff9800',
      light: '#ffb74d',
      dark: '#f57c00',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h3: {
      fontWeight: 700,
      fontSize: '2.5rem',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      backgroundClip: 'text',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
    },
    h4: {
      fontWeight: 600,
      fontSize: '2rem',
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.5rem',
    },
    h6: {
      fontWeight: 500,
      fontSize: '1.25rem',
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.6,
    },
  },
  shape: {
    borderRadius: 16,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          textTransform: 'none',
          fontWeight: 600,
          padding: '12px 24px',
          fontSize: '1rem',
        },
        contained: {
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)',
          '&:hover': {
            background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
            boxShadow: '0 12px 40px rgba(102, 126, 234, 0.4)',
            transform: 'translateY(-2px)',
          },
        },
        outlined: {
          border: '2px solid rgba(255, 255, 255, 0.2)',
          color: '#ffffff',
          '&:hover': {
            border: '2px solid rgba(255, 255, 255, 0.4)',
            background: 'rgba(255, 255, 255, 0.05)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          background: 'rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          background: 'rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
        },
      },
    },
  },
});

const EmployeeDashboard = () => {
  const { translate } = useLanguage();
  const [scannedQR, setScannedQR] = useState('');
  const [phoneDigits, setPhoneDigits] = useState('');
  const [accessMethod, setAccessMethod] = useState('qr');
  const [user, setUser] = useState(null);
  const [error, setError] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [cameraActive, setCameraActive] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [accessLogs, setAccessLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [dateRange, setDateRange] = useState({
    startDate: moment().subtract(7, 'days').format('YYYY-MM-DD'),
    endDate: moment().format('YYYY-MM-DD')
  });
  const qrReaderRef = useRef(null);

  const showSnackbar = (message, severity) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  // Network status management
  useEffect(() => {
    const removeListener = networkManager.addListener((event, online) => {
      setIsOnline(online);
      if (event === 'online') {
        showSnackbar('Conexión restaurada', 'success');
      } else if (event === 'offline') {
        showSnackbar('Sin conexión a internet', 'warning');
      }
    });

    return removeListener;
  }, []);

  const startCamera = () => {
    setCameraActive(true);
    setError('');
  };

  const stopCamera = () => {
    setCameraActive(false);
  };

  useEffect(() => {
    if (!cameraActive || !qrReaderRef.current) return;

    const scanner = new Html5QrcodeScanner(
      'qr-reader',
      {
        fps: 10,
        qrbox: 250,
        facingMode: 'environment',
      },
      false
    );

    const onScanSuccess = async (decodedText) => {
      console.log("📸 Código QR escaneado:", decodedText);
      setScannedQR(decodedText);
      setIsProcessing(true);
      await validateQRCode(decodedText);
      scanner.clear();
      stopCamera();
      setIsProcessing(false);
    };

    const onScanError = (err) => {
      if (!err.message.includes("No MultiFormat Readers were able to detect the code")) {
        console.error('🚨 Error escaneando QR:', err);
        setError('Error al escanear el código QR');
      }
    };

    scanner.render(onScanSuccess, onScanError);

    return () => {
      scanner.clear();
    };
  }, [cameraActive, user]);

  const validateQRCode = async (qrCode) => {
    if (!qrCode) return;

    try {
      const response = await networkManager.makeRequest(
        {
          method: 'POST',
          url: `${process.env.REACT_APP_API_URL}/api/validate-qr`,
          data: { qr_code: qrCode }
        },
        {
          endpoint: 'validate-qr',
          maxRetries: 3,
          storeOnFailure: true
        }
      );

      // Verificar si la respuesta tiene contenido
      const responseText = await response.text();
      console.log('📡 Respuesta de validación QR:', responseText);
      console.log('📡 Longitud de respuesta:', responseText.length);
      
      if (!responseText || responseText.trim() === '') {
        throw new Error('Respuesta vacía del servidor - El servidor no devolvió datos');
      }

      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        console.error('❌ Error parseando JSON:', parseError);
        console.error('❌ Contenido de respuesta:', responseText);
        throw new Error('Respuesta del servidor no es JSON válido');
      }

      if (data.success) {
        setUser(data.user);
        setError('');
        showSnackbar('Empleado validado exitosamente', 'success');
        await fetchEmployeeLogs(data.user.id);
      } else {
        setError('Código QR no válido');
        setUser(null);
        showSnackbar('Código QR inválido', 'error');
      }
    } catch (error) {
      console.error('❌ Error en validación:', error);
      
      if (!isOnline) {
        setError('Sin conexión a internet. La solicitud se reintentará automáticamente cuando se restaure la conexión.');
        showSnackbar('Sin conexión. Se reintentará automáticamente.', 'warning');
      } else {
        setError('Error al validar el código QR');
        showSnackbar('Error al validar el código QR', 'error');
      }
    }
  };

  const validatePhoneDigits = async (digits) => {
    if (!digits || digits.length !== 4) return;

    setIsProcessing(true);
    try {
      const response = await networkManager.makeRequest(
        {
          method: 'POST',
          url: `${process.env.REACT_APP_API_URL}/api/validate-phone`,
          data: { phone_digits: digits }
        },
        {
          endpoint: 'validate-phone',
          maxRetries: 3,
          storeOnFailure: true
        }
      );

      // Verificar si la respuesta tiene contenido
      const responseText = await response.text();
      console.log('📡 Respuesta de validación teléfono:', responseText);
      console.log('📡 Longitud de respuesta:', responseText.length);
      
      if (!responseText || responseText.trim() === '') {
        throw new Error('Respuesta vacía del servidor - El servidor no devolvió datos');
      }

      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        console.error('❌ Error parseando JSON:', parseError);
        console.error('❌ Contenido de respuesta:', responseText);
        throw new Error('Respuesta del servidor no es JSON válido');
      }

      if (data.success) {
        setUser(data.user);
        setError('');
        showSnackbar('Empleado validado exitosamente', 'success');
        await fetchEmployeeLogs(data.user.id);
      } else {
        setError('Dígitos del teléfono no válidos');
        setUser(null);
        showSnackbar('Dígitos del teléfono inválidos', 'error');
      }
    } catch (error) {
      console.error('❌ Error en validación:', error);
      
      if (!isOnline) {
        setError('Sin conexión a internet. La solicitud se reintentará automáticamente cuando se restaure la conexión.');
        showSnackbar('Sin conexión. Se reintentará automáticamente.', 'warning');
      } else {
        setError('Error al validar los dígitos del teléfono');
        showSnackbar('Error al validar los dígitos del teléfono', 'error');
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const fetchEmployeeLogs = async (userId) => {
    setLoading(true);
    try {
      const response = await networkManager.makeRequest(
        {
          method: 'GET',
          url: `${process.env.REACT_APP_API_URL}/api/access-logs?user_id=${userId}&start_date=${dateRange.startDate}&end_date=${dateRange.endDate}`
        },
        {
          endpoint: 'employee-logs',
          maxRetries: 2,
          storeOnFailure: false
        }
      );

      // Verificar si la respuesta tiene contenido
      const responseText = await response.text();
      console.log('📡 Respuesta de logs de empleado:', responseText);
      console.log('📡 Longitud de respuesta:', responseText.length);
      
      if (!responseText || responseText.trim() === '') {
        throw new Error('Respuesta vacía del servidor - El servidor no devolvió datos');
      }

      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        console.error('❌ Error parseando JSON:', parseError);
        console.error('❌ Contenido de respuesta:', responseText);
        throw new Error('Respuesta del servidor no es JSON válido');
      }
      
      setAccessLogs(data.logs || []);
    } catch (error) {
      console.error('❌ Error obteniendo logs del empleado:', error);
      showSnackbar('Error al cargar los registros de trabajo', 'error');
      setAccessLogs([]);
    } finally {
      setLoading(false);
    }
  };

  const handlePhoneInput = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, '').slice(0, 4);
    setPhoneDigits(value);
    
    if (value.length === 4) {
      validatePhoneDigits(value);
    }
  };

  const resetForm = () => {
    setUser(null);
    setError('');
    setPhoneDigits('');
    setScannedQR('');
    setAccessLogs([]);
    stopCamera();
  };

  const calculateTotalHours = (log) => {
    if (!log.workstation_entry_time || !log.workday_end) return 'N/A';
    
    const entry = moment(log.workstation_entry_time);
    const exit = moment(log.workday_end);
    const duration = moment.duration(exit.diff(entry));
    const hours = duration.asHours();
    
    return `${hours.toFixed(2)}h`;
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return 'N/A';
    return moment(timestamp).format('HH:mm');
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    return moment(timestamp).format('DD/MM/YYYY');
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDateRangeChange = (field, value) => {
    setDateRange(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const refreshLogs = () => {
    if (user) {
      fetchEmployeeLogs(user.id);
    }
  };

  return (
    <ThemeProvider theme={darkTheme}>
      <CssBaseline />
      <Box 
        sx={{ 
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a2e 50%, #16213e 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 3,
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        {/* Background decorative elements */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'radial-gradient(circle at 20% 80%, rgba(102, 126, 234, 0.1) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(118, 75, 162, 0.1) 0%, transparent 50%)',
            pointerEvents: 'none',
          }}
        />

        <Box sx={{ maxWidth: 1200, width: '100%', position: 'relative', zIndex: 1 }}>
          {/* Network Status */}
          <Box sx={{ position: 'absolute', top: 16, right: 16, zIndex: 10 }}>
            <Tooltip title={isOnline ? 'Conectado' : 'Sin conexión'}>
              <Chip
                icon={isOnline ? <Wifi /> : <WifiOff />}
                label={isOnline ? 'Online' : 'Offline'}
                color={isOnline ? 'success' : 'error'}
                size="small"
                variant="outlined"
              />
            </Tooltip>
          </Box>

          {/* Header */}
          <Fade in timeout={1000}>
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Typography variant="h3" sx={{ mb: 2 }}>
                Core City Group
              </Typography>
              <Typography variant="h5" sx={{ color: 'text.secondary', fontWeight: 400 }}>
                Mi Tabla de Trabajo
              </Typography>
            </Box>
          </Fade>

          {!user ? (
            <Zoom in timeout={800}>
              <Card sx={{ p: 4, mb: 3 }}>
                <CardContent>
                  <Typography variant="h5" sx={{ mb: 3, textAlign: 'center', fontWeight: 600 }}>
                    Acceder a mi tabla de trabajo
                  </Typography>

                  <Tabs
                    value={accessMethod}
                    onChange={(e, newValue) => setAccessMethod(newValue)}
                    centered
                    sx={{ 
                      mb: 4,
                      '& .MuiTab-root': {
                        fontSize: '1.1rem',
                        fontWeight: 600,
                        minHeight: 60,
                      },
                      '& .Mui-selected': {
                        color: '#667eea !important',
                      },
                      '& .MuiTabs-indicator': {
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        height: 3,
                        borderRadius: 2,
                      },
                    }}
                  >
                    <Tab 
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <QrCodeScanner />
                          Código QR
                        </Box>
                      } 
                      value="qr" 
                    />
                    <Tab 
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Phone />
                          Teléfono
                        </Box>
                      } 
                      value="phone" 
                    />
                  </Tabs>

                  {accessMethod === 'qr' ? (
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
                        Escanea tu código QR para acceder a tu tabla de trabajo
                      </Typography>
                      
                      {!cameraActive ? (
                        <Button
                          variant="contained"
                          size="large"
                          startIcon={<QrCodeScanner />}
                          onClick={startCamera}
                          sx={{ 
                            py: 2, 
                            px: 4, 
                            fontSize: '1.1rem',
                            minWidth: 200
                          }}
                        >
                          Activar Escáner QR
                        </Button>
                      ) : (
                        <Box sx={{ position: 'relative' }}>
                          <Paper 
                            elevation={8} 
                            sx={{ 
                              p: 3, 
                              background: 'rgba(0, 0, 0, 0.8)',
                              border: '2px solid rgba(102, 126, 234, 0.3)',
                              borderRadius: 3
                            }}
                          >
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                              <Typography variant="h6" sx={{ color: 'primary.main' }}>
                                Escáner QR Activo
                              </Typography>
                              <IconButton onClick={stopCamera} color="error">
                                <Close />
                              </IconButton>
                            </Box>
                            <div id="qr-reader" ref={qrReaderRef} style={{ width: '100%' }}></div>
                          </Paper>
                        </Box>
                      )}
                    </Box>
                  ) : (
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
                        Ingresa los últimos 4 dígitos de tu teléfono
                      </Typography>
                      
                      <Box sx={{ maxWidth: 300, mx: 'auto' }}>
                        <input
                          type="text"
                          value={phoneDigits}
                          onChange={handlePhoneInput}
                          placeholder="0000"
                          maxLength={4}
                          style={{
                            width: '100%',
                            padding: '16px 20px',
                            fontSize: '2rem',
                            textAlign: 'center',
                            background: 'rgba(255, 255, 255, 0.1)',
                            border: '2px solid rgba(255, 255, 255, 0.2)',
                            borderRadius: '12px',
                            color: '#ffffff',
                            outline: 'none',
                            fontFamily: 'monospace',
                            letterSpacing: '8px',
                            fontWeight: 'bold',
                          }}
                        />
                        <Typography variant="caption" sx={{ mt: 1, display: 'block', color: 'text.secondary' }}>
                          Solo números del 0 al 9
                        </Typography>
                      </Box>
                    </Box>
                  )}

                  {isProcessing && (
                    <Box sx={{ mt: 3, textAlign: 'center' }}>
                      <LinearProgress sx={{ mb: 2 }} />
                      <Typography variant="body2" color="text.secondary">
                        Procesando...
                      </Typography>
                    </Box>
                  )}

                  {error && (
                    <Grow in timeout={300}>
                      <Alert 
                        severity="error" 
                        sx={{ mt: 3 }}
                        action={
                          <IconButton
                            color="inherit"
                            size="small"
                            onClick={() => setError('')}
                          >
                            <Close fontSize="inherit" />
                          </IconButton>
                        }
                      >
                        {error}
                      </Alert>
                    </Grow>
                  )}
                </CardContent>
              </Card>
            </Zoom>
          ) : (
            <Slide direction="up" in timeout={500}>
              <Card sx={{ p: 4 }}>
                <CardContent>
                  {/* Employee Info Header */}
                  <Box sx={{ textAlign: 'center', mb: 4 }}>
                    <Avatar 
                      src={user.photo_url} 
                      sx={{ 
                        width: 80, 
                        height: 80, 
                        mx: 'auto', 
                        mb: 2,
                        border: '4px solid rgba(102, 126, 234, 0.3)',
                        boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)'
                      }} 
                    />
                    <Typography variant="h4" sx={{ color: 'text.primary', mb: 1, fontWeight: 600 }}>
                      {user.name}
                    </Typography>
                    <Typography variant="h6" sx={{ color: 'text.secondary', mb: 2 }}>
                      {user.position}
                    </Typography>
                    <Chip 
                      icon={<CheckCircle />} 
                      label="Empleado Verificado" 
                      color="success" 
                      variant="outlined"
                      sx={{ fontWeight: 600 }}
                    />
                  </Box>

                  {/* Date Range Selector */}
                  <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center', justifyContent: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CalendarToday fontSize="small" />
                      <input
                        type="date"
                        value={dateRange.startDate}
                        onChange={(e) => handleDateRangeChange('startDate', e.target.value)}
                        style={{
                          padding: '8px 12px',
                          background: 'rgba(255, 255, 255, 0.1)',
                          border: '1px solid rgba(255, 255, 255, 0.2)',
                          borderRadius: '8px',
                          color: '#ffffff',
                          fontSize: '14px'
                        }}
                      />
                    </Box>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      hasta
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <input
                        type="date"
                        value={dateRange.endDate}
                        onChange={(e) => handleDateRangeChange('endDate', e.target.value)}
                        style={{
                          padding: '8px 12px',
                          background: 'rgba(255, 255, 255, 0.1)',
                          border: '1px solid rgba(255, 255, 255, 0.2)',
                          borderRadius: '8px',
                          color: '#ffffff',
                          fontSize: '14px'
                        }}
                      />
                    </Box>
                    <Button
                      variant="outlined"
                      startIcon={<Refresh />}
                      onClick={refreshLogs}
                      disabled={loading}
                      size="small"
                    >
                      Actualizar
                    </Button>
                  </Box>

                  {/* Work Logs Table */}
                  {loading ? (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <LinearProgress sx={{ mb: 2 }} />
                      <Typography variant="body2" color="text.secondary">
                        Cargando registros de trabajo...
                      </Typography>
                    </Box>
                  ) : accessLogs.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <Schedule sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                      <Typography variant="h6" color="text.secondary">
                        No hay registros de trabajo para el período seleccionado
                      </Typography>
                    </Box>
                  ) : (
                    <TableContainer component={Paper} sx={{ background: 'rgba(255, 255, 255, 0.05)' }}>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 600, color: 'primary.main' }}>Fecha</TableCell>
                            <TableCell sx={{ fontWeight: 600, color: 'primary.main' }}>Entrada</TableCell>
                            <TableCell sx={{ fontWeight: 600, color: 'primary.main' }}>Salida</TableCell>
                            <TableCell sx={{ fontWeight: 600, color: 'primary.main' }}>Horas Totales</TableCell>
                            <TableCell sx={{ fontWeight: 600, color: 'primary.main' }}>Estado</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {accessLogs
                            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                            .map((log, index) => (
                            <TableRow key={index} hover>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {formatDate(log.workstation_entry_time || log.timestamp)}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Work fontSize="small" color="success" />
                                  <Typography variant="body2">
                                    {formatTime(log.workstation_entry_time)}
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <ExitToApp fontSize="small" color="error" />
                                  <Typography variant="body2">
                                    {formatTime(log.workday_end)}
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <AccessTime fontSize="small" color="primary" />
                                  <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                                    {calculateTotalHours(log)}
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={log.workday_end ? "Jornada Completa" : "En Trabajo"}
                                  color={log.workday_end ? "success" : "warning"}
                                  size="small"
                                  variant="outlined"
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                      <TablePagination
                        rowsPerPageOptions={[5, 10, 25]}
                        component="div"
                        count={accessLogs.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        labelRowsPerPage="Registros por página:"
                        labelDisplayedRows={({ from, to, count }) => 
                          `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`
                        }
                      />
                    </TableContainer>
                  )}

                  {/* Action Buttons */}
                  <Box sx={{ textAlign: 'center', mt: 4 }}>
                    <Button
                      variant="outlined"
                      startIcon={<Refresh />}
                      onClick={resetForm}
                      sx={{ px: 4 }}
                    >
                      Cambiar Usuario
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Slide>
          )}
        </Box>

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={4000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert 
            onClose={handleSnackbarClose} 
            severity={snackbarSeverity} 
            sx={{ 
              width: '100%',
              borderRadius: 2,
              fontWeight: 600,
            }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Box>
    </ThemeProvider>
  );
};

export default EmployeeDashboard;
